// useRecipients.js — v2 "Email-only invites"
// Manages the staged recipient list. Each recipient carries the full GlobalUser
// profile payload so RecipientCard can render avatars and navigate to profiles.
//
// CHANGES from v1:
//   · SMS/phone invites for new people are BLOCKED at the data layer:
//       - addManual() rejects phone numbers with a clear toast
//       - addFromCSV() skips rows whose contactType is "phone" and reports it
//   · This is the enforcement point — even if some UI still passes a phone,
//     no phone-kind recipient can be staged anymore.
//   · Internal Blitz users (addFromSearch) are unaffected.

import { useState, useCallback } from "react";
import { toast } from "sonner";
import { isValidEmail, isValidPhone } from "./T2a.utils";

/**
 * Recipient shape
 * ───────────────
 * {
 *   id            : string,           // dedupe key  "gu:<id>" | "email:<e>"
 *   kind          : "internal" | "email",   // "phone" no longer creatable
 *   globalUserId  : number | null,
 *   label         : string,           // display name
 *   username      : string | null,    // @handle (for profile link)
 *   email         : string | null,
 *   phoneNumber   : string | null,    // kept on internal users for display only
 *   pfphref       : string | null,    // avatar URL
 *   userType      : string | null,    // "creator" | "user"
 *   creatorUsername: string | null,   // if creator account
 *   amount        : string,           // raw input value
 *   fromCSV       : boolean,
 * }
 */
export function useRecipients() {
  const [recipients, setRecipients] = useState([]);

  // ── from search result (internal Blitz users — unchanged) ──────────────────
  const addFromSearch = useCallback((result) => {
    const key = `gu:${result.global_user_id}`;
    if (recipients.some((r) => r.id === key)) {
      toast.error("Already added");
      return false;
    }
    setRecipients((prev) => [
      ...prev,
      {
        id:             key,
        kind:           "internal",
        globalUserId:   result.global_user_id,
        label:          result.display_name || result.creator || "User",
        username:       result.creator || result.creator_username || null,
        email:          result.email || null,
        phoneNumber:    result.phone_number || null,
        pfphref:        result.pfphref || null,
        userType:       result.user_type || null,
        creatorUsername: result.creator_username || result.creator || null,
        amount:         "",
        fromCSV:        false,
      },
    ]);
    return true;
  }, [recipients]);

  // ── manual invite — EMAIL ONLY ──────────────────────────────────────────────
  const addManual = useCallback((label, contact, initialAmount = "") => {
    const c = (contact || "").trim();
    if (!c) { toast.error("Enter a valid email address"); return false; }

    // Phone numbers are explicitly rejected — SMS invites are not supported.
    if (isValidPhone(c) && !isValidEmail(c)) {
      toast.error("SMS invites aren't supported", {
        description: "Add this person with their email address instead.",
      });
      return false;
    }

    if (isValidEmail(c)) {
      const key = `email:${c.toLowerCase()}`;
      if (recipients.some((r) => r.id === key)) { toast.error("Already added"); return false; }
      setRecipients((prev) => [
        ...prev,
        { id: key, kind: "email", globalUserId: null, label: label || c,
          username: null, email: c.toLowerCase(), phoneNumber: null,
          pfphref: null, userType: null, creatorUsername: null,
          amount: initialAmount, fromCSV: false },
      ]);
      return true;
    }

    toast.error("Enter a valid email address");
    return false;
  }, [recipients]);

  // ── CSV import — phone rows are skipped ─────────────────────────────────────
  const addFromCSV = useCallback((rows) => {
    const existing = new Set(
      recipients.map((r) => r.email || "").filter(Boolean)
    );

    // Drop phone rows entirely — only email contacts can be invited.
    const phoneRows = rows.filter((row) => row.contactType !== "email");
    const emailRows = rows.filter((row) => row.contactType === "email");

    const fresh = emailRows
      .map((row) => ({
        id:             `email:${(row.contact || "").toLowerCase()}`,
        kind:           "email",
        globalUserId:   null,
        label:          row.name,
        username:       null,
        email:          (row.contact || "").toLowerCase(),
        phoneNumber:    null,
        pfphref:        null,
        userType:       null,
        creatorUsername: null,
        amount:         row.amount,
        fromCSV:        true,
      }))
      .filter((r) => r.email && !existing.has(r.email));

    const dupes = emailRows.length - fresh.length;

    setRecipients((prev) => [...prev, ...fresh]);

    if (phoneRows.length > 0) {
      toast.warning(
        `${phoneRows.length} phone-number row${phoneRows.length !== 1 ? "s" : ""} skipped`,
        { description: "SMS invites aren't supported — use email addresses in your CSV." }
      );
    }
    toast.success(
      dupes > 0
        ? `Imported ${fresh.length} · ${dupes} duplicate${dupes !== 1 ? "s" : ""} skipped`
        : `Imported ${fresh.length} recipient${fresh.length !== 1 ? "s" : ""}`
    );
  }, [recipients]);

  const remove    = useCallback((id) => setRecipients((prev) => prev.filter((r) => r.id !== id)), []);
  const setAmount = useCallback((id, value) => setRecipients((prev) => prev.map((r) => r.id === id ? { ...r, amount: value } : r)), []);
  const clear     = useCallback(() => setRecipients([]), []);

  return { recipients, addFromSearch, addManual, addFromCSV, remove, setAmount, clear };
}